import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, Calendar, Cpu } from 'lucide-react';
import { useLanguage, LanguageSwitcher } from '@/components/LanguageContext';

const Article9 = () => {
  const { t } = useLanguage();
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-white">
      <div className="container mx-auto px-6 py-8">
        {/* Header */}
        <div className="mb-8">
          <Link to="/">
            <Button variant="outline" className="mb-4">
              <ArrowLeft className="h-4 w-4 mr-2" />
              {t('common.backToHome')}
            </Button>
          </Link>
          
          <div className="flex items-center mb-4">
            <Badge className="bg-green-600 text-white mr-4">
              <Cpu className="h-3 w-3 mr-1" />
              {t('category.digitaltwin')}
            </Badge>
            <span className="text-sm text-gray-600">PepsiCo</span>
          </div>
          
          <div className="flex items-center text-sm text-muted-foreground mb-6">
            <Calendar className="h-4 w-4 mr-1" />
            January 8, 2026
            <span className="mx-2">•</span>
            <span>8 min read</span>
          </div>
          
          <div className="flex justify-end mb-4">
            <LanguageSwitcher />
          </div>
        </div>

        {/* Article Content */}
        <article className="max-w-4xl mx-auto">
          <h1 className="text-4xl font-bold text-gray-900 mb-6">
            {t('article.9.title')}
          </h1>
          
          <img 
            src="https://assets1.consumergoods.com/images/v/max_width_640/2026-01/pepsico_digital_twin.jpg" 
            alt="PepsiCo digital twin technology and supply chain optimization"
            className="w-full h-64 object-cover rounded-lg mb-8"
          />

          <div className="prose prose-lg max-w-none">
            <p className="text-xl text-gray-700 mb-6 font-medium">
              {t('article.9.intro')}
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">{t('article.9.section1.title')}</h2>
            
            <p className="mb-6">
              {t('article.9.section1.content')}
            </p>

            <p className="mb-6">
              {t('article.9.detail1')}
            </p>

            <p className="mb-6">
              {t('article.9.detail2')}
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">{t('article.9.section2.title')}</h2>
            
            <p className="mb-6">
              {t('article.9.section2.content')}
            </p>

            <p className="mb-6">
              {t('article.9.detail3')}
            </p>

            <p className="mb-6">
              {t('article.9.detail4')}
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">{t('article.9.section3.title')}</h2>
            
            <p className="mb-6">
              {t('article.9.section3.content')}
            </p>

            <p className="mb-6">
              {t('article.9.detail5')}
            </p>

            <p className="mb-6">
              {t('article.9.detail6')}
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">{t('article.9.section4.title')}</h2>
            
            <p className="mb-6">
              {t('article.9.section4.content')}
            </p>

            <p className="mb-6">
              {t('article.9.detail7')}
            </p>

            <p className="mb-6">
              {t('article.9.detail8')}
            </p>

            <div className="bg-green-50 p-6 rounded-lg mt-8">
              <h3 className="font-bold text-gray-900 mb-2">Digital Twin Technology Benefits</h3>
              <ul className="space-y-2 text-sm text-gray-700">
                <li><strong>Real-time Monitoring:</strong> Continuous facility operations visibility</li>
                <li><strong>Predictive Analysis:</strong> AI-powered production process optimization</li>
                <li><strong>Scenario Simulation:</strong> Virtual testing before physical implementation</li>
                <li><strong>Cost Reduction:</strong> Minimized disruptions and operational efficiency</li>
                <li><strong>Smart Planning:</strong> AI co-designers for facility optimization</li>
                <li><strong>Sustainability Focus:</strong> Environmental impact assessment capabilities</li>
              </ul>
            </div>
          </div>

          {/* Source Attribution */}
          <div className="mt-12 p-6 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-600 mb-2">
              {t('common.sourceAttribution')} PepsiCo official technology reports.
            </p>
            <p className="text-sm text-gray-600 mb-4">
              {t('common.sourceDetails')}
            </p>
            <a 
              href="https://consumergoods.com/pepsico-get-ahead-supply-chain-challenges-3d-digital-twin-tech" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-block"
            >
              <Button className="bg-blue-600 hover:bg-blue-700 text-white">
                {t('common.readOriginalArticle')}
              </Button>
            </a>
          </div>

          {/* Navigation */}
          <div className="mt-8 flex justify-between">
            <Link to="/">
              <Button variant="outline">
                <ArrowLeft className="h-4 w-4 mr-2" />
                {t('common.backToAllArticles')}
              </Button>
            </Link>
          </div>
        </article>
      </div>
    </div>
  );
};

export default Article9;